import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { AdminTrainer } from "../types/AdminTypes";
import axios from "axios";
import { Config } from "../API/config";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import TrainerViewCard from "./TrainerViewCard";

const AcceptingTrainerCard = ({ trainer, update }: { trainer: AdminTrainer; update: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [updatedTrainer, setUpdatedTrainer] = useState({
        name: trainer.name,
        email: trainer.email,
        phone: trainer.phone,
        gender: trainer.gender,
        governorate: trainer.governorate,
        city: trainer.city,
        yearsOfExperience: trainer.trainerId.yearsOfExperience,
        age: trainer.trainerId.age,
        numberOfLessonsOnWeek: trainer.trainerId.numberOfLessonsOnWeek,
        priceOfLesson: trainer.trainerId.priceOfLesson,
        needs: trainer.trainerId.needs,
        serviceGovernorates: trainer.trainerId.serviceGovernorates,
        serviceCities: trainer.trainerId.serviceCities,
        category: trainer.trainerId.category,
        subCategory: trainer.trainerId.subCategory,
        notice: trainer.trainerId.notice,
        type: trainer.trainerId.type,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setUpdatedTrainer((prev) => ({ ...prev, [name]: value }));
    };

    const handleTrainerUpdate = async () => {
        try {
            let { data } = await axios.patch(`${Config.editTrainer}/${trainer._id}`, updatedTrainer, {
                withCredentials: true,
            });
            if (data.statusCode === 200) {
                toast.success("تم تحديث بيانات المدرب");
                update();
                setIsOpen(false);
            }
        } catch (err) {
            console.error("Error updating trainer:", err);
            toast.error("حدث خطأ أثناء تحديث بيانات المدرب");
        }
    };

    const acceptTrainer = async () => {
        try {
            let { data } = await axios.patch(`${Config.acceptTrainer}/${trainer._id}`, {}, { withCredentials: true });
            if (data.statusCode === 201) {
                toast.success("تم قبول المدرب");
                update();
            }
        } catch (err) {
            console.error("Error accepting trainer:", err);
            toast.error("حدث خطأ أثناء قبول المدرب");
        }
    };

    const rejectTrainer = async () => {
        try {
            let { data } = await axios.patch(`${Config.rejectTrainer}/${trainer._id}`, {}, { withCredentials: true });
            if (data.statusCode === 201) {
                toast.success("تم رفض المدرب");
                update();
            }
        } catch (err) {
            console.error("Error rejecting trainer:", err);
            toast.error("حدث خطأ أثناء رفض المدرب");
        }
    };

    return (
        <div className="p-6 shadow-lg rounded-xl border border-gray-200 bg-white text-right">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
                <img src={trainer.profileImage} alt="Trainer Avatar" className="w-12 h-12 rounded-full border border-gray-300" />
                <div>
                    <h2 className="text-lg font-semibold">{trainer.name}</h2>
                    <p className="text-sm text-gray-600">{trainer.email}</p>
                </div>
            </div>
            <div className="mt-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                    <p><span className="font-semibold">المنطقة:</span> {trainer.city}</p>
                    <p><span className="font-semibold">المحافظة:</span> {trainer.governorate}</p>
                    <p><span className="font-semibold">عدد الحصص:</span> {trainer.trainerId.numberOfLessonsOnWeek}</p>
                    <p><span className="font-semibold">السن:</span> {trainer.trainerId.age} سنة</p>
                    <p><span className="font-semibold">رقم التليفون:</span> {trainer.phone}</p>
                    <p><span className="font-semibold">النوع:</span> {trainer.gender}</p>
                    <p><span className="font-semibold">سنوات الخبرة:</span> {trainer.trainerId.yearsOfExperience}</p>
                    <p><span className="font-semibold">الفئة:</span> {trainer.trainerId.category}</p>
                    <p><span className="font-semibold">الفئة الفرعية:</span> {trainer.trainerId.subCategory}</p>
                    <p><span className="font-semibold">النوع:</span> {trainer.trainerId.type}</p>
                    <p className="text-gray-600 mt-4">
                        <span className="font-semibold block">:احتياجات الكورس</span>
                        <span>{trainer.trainerId.needs}</span>
                    </p>
                    <p className="text-gray-600 mt-2">
                        <span className="font-semibold block">:ملاحظات</span>
                        <span>{trainer.trainerId.notice || "لا توجد ملاحظات"}</span>
                    </p>
                    <p className="text-gray-600 mt-2">
                        <span className="font-semibold block">:محافظات الخدمة</span>
                        <span>{trainer.trainerId.serviceGovernorates.join(", ") || "غير محدد"}</span>
                    </p>
                    <p className="text-gray-600 mt-2">
                        <span className="font-semibold block">:مدن الخدمة</span>
                        <span>{trainer.trainerId.serviceCities.join(", ") || "غير محدد"}</span>
                    </p>
                </div>

                <div className="flex justify-end items-center text-green-700 p-3 rounded-md mt-4">
                    <span className="font-semibold">{trainer.trainerId.priceOfLesson} ج.م / حصة</span>
                </div>
                <div className="flex justify-end mt-4 gap-3">
                    <button
                        onClick={() => setIsOpen(true)}
                        className="border border-gray-300 px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-100"
                    >
                        <Pencil size={16} /> تعديل
                    </button>
                    <button
                        onClick={acceptTrainer}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                    >
                        <Check size={16} /> قبول المدرب
                    </button>
                    <button
                        onClick={rejectTrainer}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700"
                    >
                        <X size={16} /> رفض المدرب
                    </button>
                </div>
            </div>

            {/* Modal */}
            {isOpen && (
                <motion.div
                    initial={{ x: "-100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "-100%" }}
                    transition={{ duration: 0.5 }}
                    className="fixed top-0 left-0 w-96 h-full bg-white shadow-lg p-4 overflow-y-auto z-50"
                >
                    <button
                        className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
                        onClick={() => setIsOpen(false)}
                    >
                        <X size={20} />
                    </button>
                    <TrainerViewCard id={trainer._id} />
                </motion.div>
            )}
        </div>
    );
};

// Custom Input Component
const CustomInput = ({
    name,
    label,
    value,
    onChange,
}: {
    name: string;
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
    <div className="text-right">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
            type="text"
            name={name}
            value={value}
            onChange={onChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
    </div>
);

// Custom Number Input Component
const CustomNumberInput = ({
    name,
    label,
    value,
    onChange,
}: {
    name: string;
    label: string;
    value: number;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
    <div className="text-right">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
            type="number"
            name={name}
            value={value}
            onChange={onChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
    </div>
);

// Custom Button Component
const CustomButton = ({ text, onClick, variant = "primary" }: { text: string; onClick: () => void; variant?: "primary" | "outline" }) => {
    const baseStyle = "px-4 py-2 rounded-lg font-medium flex items-center justify-center";
    const styles = {
        primary: `${baseStyle} bg-blue-600 text-white hover:bg-blue-700`,
        outline: `${baseStyle} border border-gray-300 text-gray-700 hover:bg-gray-100`,
    };

    return <button onClick={onClick} className={styles[variant]}>{text}</button>;
};

export default AcceptingTrainerCard;